import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  CreditCard, 
  Settings, 
  Plus, 
  Edit2, 
  Trash2,
  Check,
  X,
  Eye,
  EyeOff,
  ArrowLeft,
  Loader2,
  Shield,
  Zap,
  Crown,
  AlertTriangle,
  Infinity,
  Mail,
  Send,
  Phone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlatformSettingsPage({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("stripe");
  const [loading, setLoading] = useState(true);
  
  // Stripe settings state
  const [stripeSettings, setStripeSettings] = useState(null);
  const [stripeForm, setStripeForm] = useState({
    secret_key: "",
    publishable_key: "",
    webhook_secret: "",
    is_live_mode: false
  });
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [savingStripe, setSavingStripe] = useState(false);
  
  // General settings state
  const [generalSettings, setGeneralSettings] = useState({ default_trial_days: 7 });
  const [savingGeneral, setSavingGeneral] = useState(false);
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailForm, setEmailForm] = useState({
    provider: "resend",
    api_key: "",
    sender_email: "",
    smtp_host: "",
    smtp_port: "587",
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true
  });
  const [showEmailKey, setShowEmailKey] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  
  // Plans state
  const [plans, setPlans] = useState([]);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    name: "",
    description: "",
    price_monthly: "",
    price_per_seat: "",
    daily_leads: "",
    max_seats: "",
    min_seats: "1",
    trial_days: "7",
    features: "",
    is_active: true,
    is_popular: false,
    team_discounts: ""
  });

  useEffect(() => {
    // Redirect if not platform owner
    if (user && user.system_role !== "platform_owner") {
      navigate("/dashboard");
      toast.error("Access denied. Platform owner only.");
    } else {
      fetchData();
    }
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [stripeRes, generalRes, plansRes, emailRes] = await Promise.all([
        fetch(`${API}/platform/settings/stripe`, { credentials: "include" }),
        fetch(`${API}/platform/settings/general`, { credentials: "include" }),
        fetch(`${API}/platform/plans`, { credentials: "include" }),
        fetch(`${API}/platform/settings/email`, { credentials: "include" })
      ]);

      if (stripeRes.ok) {
        const data = await stripeRes.json();
        setStripeSettings(data);
        setStripeForm(prev => ({
          ...prev,
          is_live_mode: data.is_live_mode || false,
          publishable_key: data.publishable_key || ""
        }));
      }

      if (generalRes.ok) {
        const data = await generalRes.json();
        setGeneralSettings(data);
      }

      if (plansRes.ok) {
        const data = await plansRes.json();
        setPlans(data.plans || []);
      }

      if (emailRes.ok) {
        const data = await emailRes.json();
        setEmailSettings(data);
        setEmailForm(prev => ({
          ...prev,
          provider: data.provider || "resend",
          sender_email: data.sender_email || ""
        }));
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStripe = async () => {
    // Validate keys if provided
    if (stripeForm.secret_key) {
      if (stripeForm.is_live_mode && !stripeForm.secret_key.startsWith("sk_live_")) {
        toast.error("Live mode requires sk_live_ key");
        return;
      }
      if (!stripeForm.is_live_mode && !stripeForm.secret_key.startsWith("sk_test_")) {
        toast.error("Test mode requires sk_test_ key");
        return;
      }
    }

    setSavingStripe(true);
    try {
      const response = await fetch(`${API}/platform/settings/stripe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(stripeForm)
      });

      if (response.ok) {
        toast.success("Stripe settings saved");
        // Clear secret key from form for security
        setStripeForm(prev => ({ ...prev, secret_key: "" }));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save Stripe settings");
      }
    } catch (error) {
      toast.error("Failed to save Stripe settings");
    } finally {
      setSavingStripe(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSavingGeneral(true);
    try {
      const response = await fetch(`${API}/platform/settings/general`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(generalSettings)
      });

      if (response.ok) {
        toast.success("Settings saved");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleSaveEmail = async () => {
    if (emailForm.provider !== "smtp" && !emailForm.api_key && !emailSettings?.has_api_key) {
      toast.error("API key is required");
      return;
    }
    if (emailForm.provider === "smtp" && (!emailForm.smtp_host || !emailForm.smtp_port)) {
      toast.error("SMTP host and port are required");
      return;
    }

    setSavingEmail(true);
    try {
      const response = await fetch(`${API}/platform/settings/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(emailForm)
      });

      if (response.ok) {
        toast.success("Email settings saved");
        setEmailForm(prev => ({ ...prev, api_key: "", smtp_password: "" }));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save email settings");
      }
    } catch (error) {
      toast.error("Failed to save email settings");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setTestingEmail(true);
    try {
      const response = await fetch(`${API}/platform/settings/email/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ test_email: testEmailAddress })
      });

      if (response.ok) {
        toast.success(`Test email sent to ${testEmailAddress}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Test email failed");
      }
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  const openPlanDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name,
        description: plan.description || "",
        price_monthly: plan.price_monthly?.toString() || "",
        price_per_seat: plan.price_per_seat?.toString() || "",
        daily_leads: plan.daily_leads?.toString() || "",
        max_seats: plan.max_seats?.toString() || "",
        min_seats: plan.min_seats?.toString() || "1",
        trial_days: plan.trial_days?.toString() || "7",
        features: (plan.features || []).join("\n"),
        is_active: plan.is_active !== false,
        is_popular: plan.is_popular || false,
        team_discounts: plan.team_discounts ? 
          Object.entries(plan.team_discounts).map(([k, v]) => `${k}:${v}`).join("\n") : ""
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: "",
        description: "",
        price_monthly: "",
        price_per_seat: "",
        daily_leads: "100",
        max_seats: "50",
        min_seats: "1",
        trial_days: "7",
        features: "",
        is_active: true,
        is_popular: false,
        team_discounts: ""
      });
    }
    setPlanDialogOpen(true);
  };

  const handleSavePlan = async () => {
    if (!planForm.name || !planForm.price_monthly || !planForm.price_per_seat) {
      toast.error("Please fill in required fields");
      return;
    }

    // Parse team discounts
    const teamDiscounts = {};
    if (planForm.team_discounts) {
      planForm.team_discounts.split("\n").forEach(line => {
        const [seats, price] = line.split(":").map(s => s.trim());
        if (seats && price) {
          teamDiscounts[seats] = parseFloat(price);
        }
      });
    }

    const planData = {
      name: planForm.name,
      description: planForm.description,
      price_monthly: parseFloat(planForm.price_monthly),
      price_per_seat: parseFloat(planForm.price_per_seat),
      daily_leads: parseInt(planForm.daily_leads) || -1,
      max_seats: parseInt(planForm.max_seats) || 50,
      min_seats: parseInt(planForm.min_seats) || 1,
      trial_days: parseInt(planForm.trial_days) || 7,
      features: planForm.features.split("\n").filter(f => f.trim()),
      is_active: planForm.is_active,
      is_popular: planForm.is_popular,
      team_discounts: teamDiscounts
    };

    setSavingPlan(true);
    try {
      const url = editingPlan 
        ? `${API}/platform/plans/${editingPlan.plan_id}`
        : `${API}/platform/plans`;
      
      const response = await fetch(url, {
        method: editingPlan ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(planData)
      });

      if (response.ok) {
        toast.success(editingPlan ? "Plan updated" : "Plan created");
        setPlanDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save plan");
      }
    } catch (error) {
      toast.error("Failed to save plan");
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;

    try {
      const response = await fetch(`${API}/platform/plans/${planId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Plan deleted");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete plan");
      }
    } catch (error) {
      toast.error("Failed to delete plan");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="platform-settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/platform-admin")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Platform Settings
          </h1>
          <p className="text-slate-500">Configure Stripe, email, plans, and platform defaults</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="stripe" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Stripe
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2" data-testid="email-settings-tab">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="twilio" className="flex items-center gap-2" data-testid="twilio-settings-tab">
            <Phone className="w-4 h-4" />
            Twilio
          </TabsTrigger>
          <TabsTrigger value="plans" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Plans
          </TabsTrigger>
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            General
          </TabsTrigger>
        </TabsList>

        {/* Stripe Settings Tab */}
        <TabsContent value="stripe" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Stripe Configuration
              </CardTitle>
              <CardDescription>
                Configure your Stripe API keys for payment processing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className="p-4 rounded-lg bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Current Status</p>
                    <p className="text-sm text-slate-500">
                      {stripeSettings?.is_configured 
                        ? `Configured (${stripeSettings?.is_live_mode ? "Live Mode" : "Test Mode"})`
                        : "Not configured"}
                    </p>
                  </div>
                  <Badge className={stripeSettings?.is_configured 
                    ? (stripeSettings?.is_live_mode ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")
                    : "bg-red-100 text-red-700"
                  }>
                    {stripeSettings?.is_configured 
                      ? (stripeSettings?.is_live_mode ? "Live" : "Test")
                      : "Not Set"}
                  </Badge>
                </div>
              </div>

              {/* Mode Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base font-medium">Live Mode</Label>
                  <p className="text-sm text-slate-500">
                    Enable to process real payments
                  </p>
                </div>
                <Switch
                  checked={stripeForm.is_live_mode}
                  onCheckedChange={(checked) => setStripeForm(prev => ({ ...prev, is_live_mode: checked }))}
                />
              </div>

              {stripeForm.is_live_mode && (
                <div className="p-4 border border-amber-200 rounded-lg bg-amber-50">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900">Live Mode Warning</p>
                      <p className="text-sm text-amber-700">
                        You are about to configure live payment processing. All transactions will be real.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Secret Key */}
              <div className="space-y-2">
                <Label>Secret Key</Label>
                <div className="relative">
                  <Input
                    type={showSecretKey ? "text" : "password"}
                    placeholder={stripeForm.is_live_mode ? "sk_live_..." : "sk_test_..."}
                    value={stripeForm.secret_key}
                    onChange={(e) => setStripeForm(prev => ({ ...prev, secret_key: e.target.value }))}
                    className="pr-10"
                    data-testid="stripe-secret-key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  {stripeSettings?.has_secret_key 
                    ? "✓ Secret key is set. Enter a new key to update."
                    : "Enter your Stripe secret key"}
                </p>
              </div>

              {/* Publishable Key */}
              <div className="space-y-2">
                <Label>Publishable Key</Label>
                <Input
                  placeholder={stripeForm.is_live_mode ? "pk_live_..." : "pk_test_..."}
                  value={stripeForm.publishable_key}
                  onChange={(e) => setStripeForm(prev => ({ ...prev, publishable_key: e.target.value }))}
                  data-testid="stripe-publishable-key"
                />
              </div>

              {/* Webhook Secret (optional) */}
              <div className="space-y-2">
                <Label>Webhook Secret (Optional)</Label>
                <Input
                  type="password"
                  placeholder="whsec_..."
                  value={stripeForm.webhook_secret}
                  onChange={(e) => setStripeForm(prev => ({ ...prev, webhook_secret: e.target.value }))}
                />
                <p className="text-xs text-slate-500">
                  For verifying webhook signatures from Stripe
                </p>
              </div>

              <Button 
                onClick={handleSaveStripe} 
                disabled={savingStripe}
                className="w-full"
                data-testid="save-stripe-btn"
              >
                {savingStripe ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4 mr-2" />
                )}
                Save Stripe Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Email Settings Tab */}
        <TabsContent value="email" className="space-y-6" data-testid="email-settings-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Configuration
              </CardTitle>
              <CardDescription>
                Configure your email delivery provider for sending verification emails, password resets, and lead outreach
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className="p-4 rounded-lg bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Current Status</p>
                    <p className="text-sm text-slate-500">
                      {emailSettings?.is_configured
                        ? `Configured via ${emailSettings?.source} (${emailSettings?.provider})`
                        : "Not configured — emails will not be sent"}
                    </p>
                    {emailSettings?.sender_email && (
                      <p className="text-xs text-slate-400 mt-1">Sender: {emailSettings.sender_email}</p>
                    )}
                  </div>
                  <Badge className={emailSettings?.is_configured
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-red-100 text-red-700"
                  } data-testid="email-status-badge">
                    {emailSettings?.is_configured ? "Active" : "Not Set"}
                  </Badge>
                </div>
              </div>

              {/* Provider Selection */}
              <div className="space-y-2">
                <Label>Email Provider</Label>
                <Select value={emailForm.provider} onValueChange={(v) => setEmailForm(prev => ({ ...prev, provider: v }))}>
                  <SelectTrigger data-testid="email-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resend">Resend</SelectItem>
                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                    <SelectItem value="smtp">SMTP (Custom)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sender Email */}
              <div className="space-y-2">
                <Label>Sender Email Address</Label>
                <Input
                  type="email"
                  placeholder="noreply@yourdomain.com"
                  value={emailForm.sender_email}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, sender_email: e.target.value }))}
                  data-testid="email-sender-input"
                />
                <p className="text-xs text-slate-500">
                  The "from" address for all outgoing emails
                </p>
              </div>

              {/* API Key (Resend / SendGrid) */}
              {emailForm.provider !== "smtp" && (
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <div className="relative">
                    <Input
                      type={showEmailKey ? "text" : "password"}
                      placeholder={emailForm.provider === "resend" ? "re_..." : "SG..."}
                      value={emailForm.api_key}
                      onChange={(e) => setEmailForm(prev => ({ ...prev, api_key: e.target.value }))}
                      className="pr-10"
                      data-testid="email-api-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEmailKey(!showEmailKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showEmailKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    {emailSettings?.has_api_key
                      ? `API key is set. Enter a new key to update.`
                      : `Enter your ${emailForm.provider === "resend" ? "Resend" : "SendGrid"} API key`}
                  </p>
                </div>
              )}

              {/* SMTP Fields */}
              {emailForm.provider === "smtp" && (
                <div className="space-y-4 p-4 border rounded-lg bg-slate-50/50">
                  <p className="text-sm font-medium text-slate-700">SMTP Configuration</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Host</Label>
                      <Input
                        placeholder="smtp.gmail.com"
                        value={emailForm.smtp_host}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, smtp_host: e.target.value }))}
                        data-testid="smtp-host-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Port</Label>
                      <Input
                        type="number"
                        placeholder="587"
                        value={emailForm.smtp_port}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, smtp_port: e.target.value }))}
                        data-testid="smtp-port-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        placeholder="user@domain.com"
                        value={emailForm.smtp_username}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, smtp_username: e.target.value }))}
                        data-testid="smtp-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        type="password"
                        placeholder="App password"
                        value={emailForm.smtp_password}
                        onChange={(e) => setEmailForm(prev => ({ ...prev, smtp_password: e.target.value }))}
                        data-testid="smtp-password-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={emailForm.smtp_use_tls}
                      onCheckedChange={(checked) => setEmailForm(prev => ({ ...prev, smtp_use_tls: checked }))}
                    />
                    <Label>Use TLS</Label>
                  </div>
                </div>
              )}

              <Button
                onClick={handleSaveEmail}
                disabled={savingEmail}
                className="w-full"
                data-testid="save-email-btn"
              >
                {savingEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Save Email Settings
              </Button>

              {/* Test Email */}
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium text-slate-700">Send Test Email</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="test@yourdomain.com"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    className="flex-1"
                    data-testid="test-email-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleTestEmail}
                    disabled={testingEmail || !emailSettings?.is_configured}
                    data-testid="send-test-email-btn"
                  >
                    {testingEmail ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Test
                  </Button>
                </div>
                {!emailSettings?.is_configured && (
                  <p className="text-xs text-amber-600">Save email settings first to enable testing</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Subscription Plans</CardTitle>
                <CardDescription>Manage your subscription plans and pricing</CardDescription>
              </div>
              <Button onClick={() => openPlanDialog()} data-testid="add-plan-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Plan
              </Button>
            </CardHeader>
            <CardContent>
              {plans.length === 0 ? (
                <div className="text-center py-8">
                  <Zap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No plans configured yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => openPlanDialog()}>
                    Create Your First Plan
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plan</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Limits</TableHead>
                      <TableHead>Trial</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plans.map((plan) => (
                      <TableRow key={plan.plan_id} data-testid={`plan-row-${plan.plan_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {plan.is_popular && <Crown className="w-4 h-4 text-amber-500" />}
                            <div>
                              <p className="font-medium">{plan.name}</p>
                              <p className="text-xs text-slate-500">{plan.plan_id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">${plan.price_per_seat}/seat</p>
                          <p className="text-xs text-slate-500">${plan.price_monthly}/mo base</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">
                            {plan.daily_leads === -1 ? (
                              <span className="flex items-center gap-1">
                                <Infinity className="w-3 h-3" /> Unlimited
                              </span>
                            ) : (
                              `${plan.daily_leads}/day`
                            )}
                          </p>
                          <p className="text-xs text-slate-500">{plan.max_seats} max seats</p>
                          {plan.min_seats > 1 && (
                            <p className="text-xs text-amber-600">{plan.min_seats} min seats</p>
                          )}
                        </TableCell>
                        <TableCell>{plan.trial_days} days</TableCell>
                        <TableCell>
                          <Badge className={plan.is_active 
                            ? "bg-emerald-100 text-emerald-700" 
                            : "bg-slate-100 text-slate-700"
                          }>
                            {plan.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openPlanDialog(plan)}
                              data-testid={`edit-plan-${plan.plan_id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeletePlan(plan.plan_id)}
                              data-testid={`delete-plan-${plan.plan_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Twilio Settings Tab */}
        <TabsContent value="twilio" className="space-y-6" data-testid="twilio-settings-content">
          <TwilioSettingsCard />
        </TabsContent>

        {/* General Settings Tab */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>Platform-wide default settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Default Trial Period (days)</Label>
                <Input
                  type="number"
                  min="0"
                  max="90"
                  value={generalSettings.default_trial_days}
                  onChange={(e) => setGeneralSettings(prev => ({ 
                    ...prev, 
                    default_trial_days: parseInt(e.target.value) || 0 
                  }))}
                  className="w-32"
                  data-testid="default-trial-days"
                />
                <p className="text-xs text-slate-500">
                  Default trial period for new subscriptions (0-90 days)
                </p>
              </div>

              <Button 
                onClick={handleSaveGeneral} 
                disabled={savingGeneral}
                data-testid="save-general-btn"
              >
                {savingGeneral ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? "Edit Plan" : "Create New Plan"}
            </DialogTitle>
            <DialogDescription>
              Configure the plan details, pricing, and features
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plan Name *</Label>
                <Input
                  value={planForm.name}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Pro"
                  data-testid="plan-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={planForm.description}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., For growing teams"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base Price ($/month) *</Label>
                <Input
                  type="number"
                  value={planForm.price_monthly}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, price_monthly: e.target.value }))}
                  placeholder="29"
                  data-testid="plan-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Price Per Seat ($/month) *</Label>
                <Input
                  type="number"
                  value={planForm.price_per_seat}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, price_per_seat: e.target.value }))}
                  placeholder="29"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Daily Leads Limit</Label>
                <Input
                  type="number"
                  value={planForm.daily_leads}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, daily_leads: e.target.value }))}
                  placeholder="100 (-1 for unlimited)"
                />
                <p className="text-xs text-slate-500">-1 for unlimited</p>
              </div>
              <div className="space-y-2">
                <Label>Max Seats</Label>
                <Input
                  type="number"
                  value={planForm.max_seats}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, max_seats: e.target.value }))}
                  placeholder="50"
                />
              </div>
              <div className="space-y-2">
                <Label>Min Seats</Label>
                <Input
                  type="number"
                  value={planForm.min_seats}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, min_seats: e.target.value }))}
                  placeholder="1"
                  data-testid="plan-min-seats"
                />
                <p className="text-xs text-slate-500">Minimum seat requirement</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Trial Days</Label>
                <Input
                  type="number"
                  value={planForm.trial_days}
                  onChange={(e) => setPlanForm(prev => ({ ...prev, trial_days: e.target.value }))}
                  placeholder="7"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Features (one per line)</Label>
              <Textarea
                value={planForm.features}
                onChange={(e) => setPlanForm(prev => ({ ...prev, features: e.target.value }))}
                placeholder="100 leads/day per user&#10;Click-to-Call&#10;SMS Outreach"
                rows={5}
              />
            </div>

            <div className="space-y-2">
              <Label>Team Discounts (seats:price per line)</Label>
              <Textarea
                value={planForm.team_discounts}
                onChange={(e) => setPlanForm(prev => ({ ...prev, team_discounts: e.target.value }))}
                placeholder="5:25&#10;10:22&#10;25:19"
                rows={3}
              />
              <p className="text-xs text-slate-500">Format: seats:price_per_seat (e.g., 5:25 means $25/seat for 5+ seats)</p>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.is_active}
                  onCheckedChange={(checked) => setPlanForm(prev => ({ ...prev, is_active: checked }))}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={planForm.is_popular}
                  onCheckedChange={(checked) => setPlanForm(prev => ({ ...prev, is_popular: checked }))}
                />
                <Label>Mark as Popular</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePlan} disabled={savingPlan} data-testid="save-plan-btn">
              {savingPlan ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {editingPlan ? "Update Plan" : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function TwilioSettingsCard() {
  const [status, setStatus] = useState(null);
  const [sid, setSid] = useState("");
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/platform/settings/twilio`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSave = async () => {
    if (!sid.trim() || !token.trim()) {
      toast.error("Account SID and Auth Token are both required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/platform/settings/twilio`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_sid: sid.trim(), auth_token: token.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Twilio credentials verified and saved");
        setSid("");
        setToken("");
        fetchStatus();
      } else {
        toast.error(data.detail || "Failed to save", { duration: 8000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear saved Twilio credentials? Will fall back to .env values.")) return;
    const res = await fetch(`${API}/platform/settings/twilio`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      toast.success("Cleared");
      fetchStatus();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="w-5 h-5" />
          Twilio Configuration
        </CardTitle>
        <CardDescription>
          Master Twilio account credentials. Used for click-to-call, recording, and phone number provisioning across all orgs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 rounded-lg bg-slate-50 flex items-start justify-between gap-4">
          <div>
            <p className="font-medium">Current Status</p>
            {loading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : status?.is_configured ? (
              <>
                <p className="text-sm text-slate-600 font-mono mt-1" data-testid="twilio-sid-display">
                  {status.account_sid_masked}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Source: <Badge variant="outline" className="text-xs">{status.source}</Badge>
                </p>
              </>
            ) : (
              <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Not configured
              </p>
            )}
          </div>
          {status?.is_configured && status?.source === "database" && (
            <Button variant="ghost" size="sm" onClick={handleClear} className="text-red-600">
              Clear
            </Button>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <Label>Account SID</Label>
            <Input
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={sid}
              onChange={(e) => setSid(e.target.value)}
              className="font-mono text-sm"
              data-testid="twilio-sid-input"
            />
            <p className="text-xs text-slate-500 mt-1">Starts with AC…. Find it at console.twilio.com.</p>
          </div>
          <div>
            <Label>Auth Token</Label>
            <div className="relative">
              <Input
                type={showToken ? "text" : "password"}
                placeholder="32-character token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="font-mono text-sm pr-10"
                data-testid="twilio-token-input"
              />
              <button
                type="button"
                onClick={() => setShowToken((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} data-testid="save-twilio-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Verify &amp; Save
            </Button>
            <a
              href="https://console.twilio.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline self-center"
            >
              Get credentials →
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
